/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */

import { DeepPartial } from 'utility-types'
import {
  ExchangeMetaData,
  MarketStat,
  PerformanceRange,
  Return,
  TValuationData,
} from '../Widgets/types'
import {
  TBEFAQ,
  TBEFAQsCategory,
} from '@/components/Common/be-driven-faqs/be-faq-response-type'
import { formatNumberToINRCommaSeparatedString } from '@/root/utils/common/numbers/format-number-to-string'

export function generateStockFaqs({
  name,
  live_price,
  symbol,
  day_high,
  day_low,
  day_open,
  prev_close,
  fiftytwo_week_high,
  fiftytwo_week_low,
  todaysTradedVolume,
  market_cap_in_currency,
  yearlyHighLow,
  day_change,
  returns,
  isNSE,
  isBSE,
  pe_ratio,
}: {
  name?: string
  live_price?: number
  symbol?: string
  day_high?: number
  day_low?: number
  day_open?: number
  prev_close?: number
  fiftytwo_week_high?: number
  fiftytwo_week_low?: number
  todaysTradedVolume?: string
  market_cap_in_currency?: number
  yearlyHighLow?: PerformanceRange
  day_change?: number
  returns?: Return[]
  isNSE?: boolean
  isBSE?: boolean
  pe_ratio?: number
}): DeepPartial<TBEFAQsCategory[]> {
  const oneMonthReturns = returns?.find(item => item.label === '1 Month Return')
    ?.value

  const threeMonthReturns = returns?.find(
    item => item.label === '3 Month Return',
  )?.value

  const oneYearReturns = returns?.find(item => item.label === '1 Year Return')
    ?.value

  const faqs: TBEFAQ[] = []

  if (name && symbol) {
    if (typeof live_price === 'number') {
      faqs.push({
        ques: `What is ${name} price today?`,
        ans: [
          {
            type: 'text',
            text: {
              label: `${name} share price today stands at ₹${live_price}, Open. ₹${day_open} ; Previous Close. ₹${prev_close} ; High. ₹${day_high} ; Low. ₹${day_low} ; 52 Week High. ₹${fiftytwo_week_high} ; 52 Week Low: ₹${fiftytwo_week_low}.`,
            },
          },
        ],
      })
    }

    faqs.push({
      ques: `How to Buy ${name} Share in India?`,
      ans: [
        {
          type: 'ul',

          list: [
            { label: 'Get a PAN card. ' },
            { label: 'Open a Demat account. ' },
            { label: 'Open a Trading account.  ' },
            { label: 'Link your Demat account to your Trading account.' },
            { label: 'Fund your Trading account.' },
            { label: `Place an order to buy ${name} shares.` },
          ],
        },
      ],
    })

    faqs.push({
      ques: `Is ${name} listed on NSE?`,
      ans: [
        {
          type: 'text',
          text: { label: `${name} is ${isNSE ? '' : 'not'} listed on NSE` },
        },
      ],
    })
    faqs.push({
      ques: `Is ${name} listed on BSE?`,
      ans: [
        {
          type: 'text',
          text: { label: `${name} is ${isBSE ? '' : 'not'} listed on BSE` },
        },
      ],
    })
    if (typeof day_high === 'number' && typeof day_low === 'number') {
      faqs.push({
        ques: `What are today’s High and Low prices of ${name}?`,
        ans: [
          {
            type: 'ul',
            list: [
              { label: `Today’s highest price of ${name} is ₹${day_high}.` },
              { label: `Today’s lowest price of ${name} is ₹${day_low}.` },
            ],
          },
        ],
      })
    }

    if (typeof pe_ratio === 'number') {
      faqs.push({
        ques: `What is the PE ratio of ${name} today?`,
        ans: [
          {
            type: 'text',
            text: {
              label: `PE Ratio of ${name} is ${pe_ratio}`,
            },
          },
          {
            type: 'text',
            text: {
              label: `PE ratio = ${name} Market price per share / ${name} Earnings per share`,
            },
          },
        ],
      })
    }
    if (todaysTradedVolume) {
      faqs.push({
        ques: `What are today’s traded volumes of ${name}?`,
        ans: [
          {
            type: 'text',
            text: {
              label: `Today’s traded volume of ${name}(${symbol}) is ${todaysTradedVolume}.`,
            },
          },
        ],
      })
    }
    if (typeof market_cap_in_currency === 'number') {
      faqs.push({
        ques: `What is today’s market capitalisation of ${name}?`,
        ans: [
          {
            type: 'text',
            text: {
              label: `Today’s market capitalisation of ${name}(${symbol}) is ₹${market_cap_in_currency}Cr.`,
            },
          },
        ],
      })
    }
    if (
      typeof yearlyHighLow?.max === 'number' &&
      typeof yearlyHighLow.min === 'number'
    ) {
      faqs.push({
        ques: `What is the 52 Week High and Low Range of ${name}?`,
        ans: [
          {
            type: 'table',
            table: {
              header: [{ label: `${name}(${symbol}` }, { label: 'Price' }],
              rows: [
                [{ label: '52 Week High' }, { label: `₹${yearlyHighLow.max}` }],
                [{ label: '52 Week Low' }, { label: `₹${yearlyHighLow.min}` }],
              ],
            },
          },
        ],
      })
    }
    if (
      typeof live_price === 'number' &&
      typeof yearlyHighLow?.max === 'number'
    ) {
      faqs.push({
        ques: `How much percentage ${name} is down from its 52 Week High?`,
        ans: [
          {
            type: 'text',
            text: {
              label: `${name}(${symbol}) share price is ₹${live_price}. It is down -${(
                ((yearlyHighLow.max - live_price) / yearlyHighLow.max) *
                100
              ).toFixed(2)}% from its 52 Week High price of ₹${
                yearlyHighLow.max
              }`,
            },
          },
        ],
      })
    }
    if (
      typeof live_price === 'number' &&
      typeof yearlyHighLow?.min === 'number'
    ) {
      faqs.push({
        ques: `How much percentage ${name} is up from its 52 Week low?`,
        ans: [
          {
            type: 'text',
            text: {
              label: `${name}(${symbol}) share price is ₹${live_price}. It is up ${(
                ((live_price - yearlyHighLow.min) / yearlyHighLow.min) *
                100
              ).toFixed(2)}% from its 52 Week Low price of ₹${
                yearlyHighLow.min
              }`,
            },
          },
        ],
      })
    }
    if (
      typeof day_change === 'number' &&
      typeof oneMonthReturns === 'number' &&
      typeof threeMonthReturns === 'number' &&
      typeof oneYearReturns === 'number'
    ) {
      faqs.push({
        ques: `What are the historical returns of ${name}?`,
        ans: [
          {
            type: 'table',
            table: {
              header: [{ label: `${name}(${symbol}` }, { label: 'Returns' }],
              rows: [
                [{ label: '1 Day Returns' }, { label: `${day_change}%` }],
                [
                  { label: '1 Month Returns' },
                  { label: `${oneMonthReturns}%` },
                ],
                [
                  { label: '3 Month Returns' },
                  { label: `${threeMonthReturns}%` },
                ],
                [{ label: '1 Year Returns' }, { label: `${oneYearReturns}%` }],
              ],
            },
          },
        ],
      })
    }
  }

  const faqsFormatted = [
    {
      faqs,
    },
  ]

  return faqsFormatted
}

export function generateEtfFaqs({
  name,
  live_price,
  day_high,
  day_low,
  day_open,
  prev_close,
  fiftytwo_week_high,
  fiftytwo_week_low,
  yearlyHighLow,
  day_change,
  returns,
  pe,
}: {
  name?: string
  live_price?: number
  symbol?: string
  day_high?: number
  day_low?: number
  day_open?: number
  prev_close?: number
  fiftytwo_week_high?: number
  fiftytwo_week_low?: number
  day_change?: number
  yearlyHighLow?: PerformanceRange
  returns?: Return[]
  pe?: number
}): DeepPartial<TBEFAQsCategory[]> {
  const oneMonthReturns = returns?.find(item => item.label === '1 Month Return')
    ?.value

  const threeMonthReturns = returns?.find(
    item => item.label === '3 Month Return',
  )?.value

  const oneYearReturns = returns?.find(item => item.label === '1 Year Return')
    ?.value

  const faqs: TBEFAQ[] = []

  if (name) {
    if (typeof live_price === 'number') {
      faqs.push({
        ques: `What is ${name} share price today?`,
        ans: [
          {
            type: 'text',
            text: {
              label: `${name} share price today stands at ₹${live_price}, Open. ₹${day_open} ; Previous Close. ₹${prev_close} ; High. ₹${day_high} ; Low. ₹${day_low} ; 52 Week High. ₹${fiftytwo_week_high} ; 52 Week Low: ₹${fiftytwo_week_low}.`,
            },
          },
        ],
      })
    }
    if (typeof day_high === 'number' && typeof day_low === 'number') {
      faqs.push({
        ques: `What are today’s High and Low prices of ${name}?`,
        ans: [
          {
            type: 'ul',
            list: [
              { label: `Today’s  highest price of ${name} is ₹${day_high}` },
              { label: `Today’s  lowest price of ${name} is ₹${day_low}` },
            ],
          },
        ],
      })
    }
    if (
      typeof yearlyHighLow?.max === 'number' &&
      typeof yearlyHighLow.min === 'number'
    ) {
      faqs.push({
        ques: `What is the 52 Week High and Low Range of ${name}?`,
        ans: [
          {
            type: 'table',
            table: {
              header: [{ label: `${name}` }, { label: 'Price' }],
              rows: [
                [{ label: '52 Week High' }, { label: `₹${yearlyHighLow.max}` }],
                [{ label: '52 Week Low' }, { label: `₹${yearlyHighLow.min}` }],
              ],
            },
          },
        ],
      })
    }
    if (
      typeof day_change === 'number' &&
      typeof oneMonthReturns === 'number' &&
      typeof threeMonthReturns === 'number' &&
      typeof oneYearReturns === 'number'
    ) {
      faqs.push({
        ques: `What are the historical returns of ${name}?`,
        ans: [
          {
            type: 'table',
            table: {
              header: [{ label: `${name}` }, { label: 'Returns' }],
              rows: [
                [{ label: '1 Day Returns' }, { label: `${day_change}%` }],
                [
                  { label: '1 Month Returns' },
                  { label: `${oneMonthReturns}%` },
                ],
                [
                  { label: '3 Month Returns' },
                  { label: `${threeMonthReturns}%` },
                ],
                [{ label: '1 Year Returns' }, { label: `${oneYearReturns}%` }],
              ],
            },
          },
        ],
      })
    }
    if (typeof pe === 'number') {
      faqs.push({
        ques: `What is the current price to earning (PE) ratio of ${name}?`,
        ans: [
          {
            type: 'text',
            text: {
              label: `${name}} price to earning (PE) ratio as on today is ${pe}`,
            },
          },
        ],
      })
    }
    faqs.push({
      ques: `How can I invest in ${name}?`,
      ans: [
        {
          type: 'text',
          text: {
            label: `You can invest via your demat account in the ${name}. You can invest either through SIP mode or lumpsum mode.`,
          },
        },
      ],
    })
    faqs.push({
      ques: `Can I invest in ${name} through a Systematic Investment Plan (SIP)?`,
      ans: [
        {
          type: 'text',
          text: {
            label: `Yes. ${name} is available for SIP on INDmoney platform for daily, weekly or monthly SIP.`,
          },
        },
      ],
    })
    faqs.push({
      ques: `What are the risks associated with investing in ${name}?`,
      ans: [
        {
          type: 'text',
          text: {
            label: `${name}'s drawbacks are not related to specifically ${name}. In Indian context, some of the ETFs have a drawbacks of higher tracking, lower trading volumes, etc.`,
          },
        },
      ],
    })
    faqs.push({
      ques: `Can I use ${name} as collateral for a loan?`,
      ans: [
        {
          type: 'text',
          text: {
            label: `Yes. ${name} can used as collateral for a loan under Loan Against Securities. `,
          },
        },
      ],
    })
    // Add after brokerage is coming from BE
    // faqs.push({
    //   ques: `What is the cost of buying ${name} at INDmoney?`,
    //   ans: [
    //     {
    //       type: 'text',
    //       text: {
    //         label: `The brokerage for buying and selling ${name} at INDmoney is 0.012%.`,
    //       },
    //     },
    //   ],
    // })
  }

  const faqsFormatted = [
    {
      faqs,
    },
  ]

  return faqsFormatted
}

/* eslint-disable max-params */
export function generateStockFaqsV2({
  name,
  live_price,
  symbol,
  day_high,
  day_low,
  day_open,
  prev_close,
  '52week_high': fiftytwo_week_high,
  '52week_low': fiftytwo_week_low,
  // valuations,
  // exchange_metadata,
  // returns,
  market_stats,
  performance_ranges,
  market_cap_in_currency, // day_change,
}: {
  name?: string
  live_price?: number
  symbol?: string
  day_high?: number
  day_low?: number
  day_open?: number
  prev_close?: number
  '52week_high'?: number
  '52week_low'?: number
  valuations?: TValuationData | null
  exchange_metadata?: ExchangeMetaData[]
  market_stats?: MarketStat[]
  performance_ranges?: PerformanceRange[]
  market_cap_in_currency?: number
  day_change?: number
  returns?: Return[]
}): { question: string; answer: string }[] {
  // const pe_ratio = valuations?.pe_ratio?.today?.ratio

  // const isNSE = exchange_metadata?.some(item => item.exchange === 'NSE')

  // const isBSE = exchange_metadata?.some(item => item.exchange === 'BSE')

  const todaysTradedVolume = market_stats?.find(item => item.label === 'Volume')
    ?.value

  const yearlyHighLow = performance_ranges?.find(
    item => item.label === '52-Week Price Range',
  )
  // const oneMonthReturns = returns?.find(item => item.label === '1 Month Return')
  //   ?.value
  // const threeMonthReturns = returns?.find(
  //   item => item.label === '3 Month Return',
  // )?.value
  // const oneYearReturns = returns?.find(item => item.label === '1 Year Return')
  //   ?.value

  const faqs: { question: string; answer: string }[] = []

  if (name && symbol) {
    if (typeof live_price === 'number') {
      faqs.push({
        question: `What is ${name} price today?`,
        answer: `<p>${name} share price today stands at ₹${live_price}, Open: ₹${day_open}, Previous Close: ₹${prev_close}, High: ₹${day_high}, Low: ₹${day_low}, 52 Week High: ₹${fiftytwo_week_high}, 52 Week Low: ₹${fiftytwo_week_low}.</p>`,
      })
    }

    faqs.push({
      question: `How to Buy ${name} Share in India?`,
      answer: `<ul>
        <li>Get a PAN card</li>
        <li>Open a Demat account</li>
        <li>Open a Trading account</li>
        <li>Link your Demat account to your Trading account</li>
        <li>Fund your Trading account</li>
        <li>Place an order to buy ${name} shares</li>
      </ul>`,
    })

    // faqs.push({
    //   question: `Is ${name} listed on NSE?`,
    //   answer: `<p>${name} is ${isNSE ? '' : 'not'} listed on NSE</p>`,
    // })

    // faqs.push({
    //   question: `Is ${name} listed on BSE?`,
    //   answer: `<p>${name} is ${isBSE ? '' : 'not'} listed on BSE</p>`,
    // })

    // if (typeof day_high === 'number' && typeof day_low === 'number') {
    //   faqs.push({
    //     question: `What are today's High and Low prices of ${name}?`,
    //     answer: `<ul>
    //       <li>Today's highest price of ${name} is ₹${day_high}.</li>
    //       <li>Today's lowest price of ${name} is ₹${day_low}.</li>
    //     </ul>`,
    //   })
    // }

    if (todaysTradedVolume) {
      faqs.push({
        question: `What are today's traded volumes of ${name}?`,
        answer: `<p>Today's traded volume of ${name}(${symbol}) is ${todaysTradedVolume}.</p>`,
      })
    }

    if (typeof market_cap_in_currency === 'number') {
      faqs.push({
        question: `What is today's market capitalisation of ${name}?`,
        answer: `<p>Today's market capitalisation of ${name}(${symbol}) is ${formatNumberToINRCommaSeparatedString(
          market_cap_in_currency,
        )} Cr.</p>`,
      })
    }

    if (
      typeof yearlyHighLow?.max === 'number' &&
      typeof yearlyHighLow.min === 'number'
    ) {
      faqs.push({
        question: `What is the 52 Week High and Low Range of ${name}?`,
        answer: `<div class="text-brand-grey-dark">
          <div class="flex justify-between items-center py-4 border-b border-gray-200 font-semibold">
            <div>${name} (${symbol})</div>
            <div>Price</div>
          </div>
          <div class="flex justify-between items-center py-4 border-b border-gray-200">
            <div>52 Week High</div>
            <div>₹${yearlyHighLow.max}</div>
          </div>
          <div class="flex justify-between items-center pt-4 border-gray-200">
            <div>52 Week Low</div>
            <div>₹${yearlyHighLow.min}</div>
          </div>
        </div>`,
      })
    }

    if (
      typeof live_price === 'number' &&
      typeof yearlyHighLow?.max === 'number'
    ) {
      faqs.push({
        question: `How much percentage ${name} is down from its 52 Week High?`,
        answer: `<p>${name} (${symbol}) share price is ₹${live_price}. It is down -${(
          ((yearlyHighLow.max - live_price) / yearlyHighLow.max) *
          100
        ).toFixed(2)}% from its 52 Week High price of ₹${
          yearlyHighLow.max
        }</p>`,
      })
    }

    if (
      typeof live_price === 'number' &&
      typeof yearlyHighLow?.min === 'number'
    ) {
      faqs.push({
        question: `How much percentage ${name} is up from its 52 Week low?`,
        answer: `<p>${name} (${symbol}) share price is ₹${live_price}. It is up ${(
          ((live_price - yearlyHighLow.min) / yearlyHighLow.min) *
          100
        ).toFixed(2)}% from its 52 Week Low price of ₹${yearlyHighLow.min}</p>`,
      })
    }

    // if (
    //   typeof day_change === 'number' &&
    //   typeof oneMonthReturns === 'number' &&
    //   typeof threeMonthReturns === 'number' &&
    //   typeof oneYearReturns === 'number'
    // ) {
    //   faqs.push({
    //     question: `What are the historical returns of ${name}?`,
    //     answer: `<div class="text-brand-grey-dark">
    //       <div class="flex justify-between items-center py-4 border-b border-gray-200 font-semibold">
    //         <div>${name} (${symbol})</div>
    //         <div>Returns</div>
    //       </div>
    //       <div class="flex justify-between items-center py-4 border-b border-gray-200">
    //         <div>1 Day Returns</div>
    //         <div>${day_change}%</div>
    //       </div>
    //       <div class="flex justify-between items-center py-4 border-b border-gray-200">
    //         <div>1 Month Returns</div>
    //         <div>${oneMonthReturns}%</div>
    //       </div>
    //       <div class="flex justify-between items-center py-4 border-b border-gray-200">
    //         <div>3 Month Returns</div>
    //         <div>${threeMonthReturns}%</div>
    //       </div>
    //       <div class="flex justify-between items-center pt-4">
    //         <div>1 Year Returns</div>
    //         <div>${oneYearReturns}%</div>
    //       </div>
    //     </div>`,
    //   })
    // }
    // if (typeof pe_ratio === 'number') {
    //   faqs.push({
    //     question: `What is the PE ratio of ${name} today?`,
    //     answer: `<p>PE Ratio of ${name} is ${pe_ratio}</p><p>PE ratio = ${name} Market price per share / ${name} Earnings per share</p>`,
    //   })
    // }
  }

  return faqs
}

export function generateEtfFaqsV2({
  name,
  live_price,
  day_high,
  day_low,
  day_open,
  prev_close,
  fiftytwo_week_high,
  fiftytwo_week_low,
  yearlyHighLow,
  day_change,
  returns,
  pe,
}: {
  name?: string
  live_price?: number
  symbol?: string
  day_high?: number
  day_low?: number
  day_open?: number
  prev_close?: number
  fiftytwo_week_high?: number
  fiftytwo_week_low?: number
  day_change?: number
  yearlyHighLow?: PerformanceRange
  returns?: Return[]
  pe?: number
}): { question: string; answer: string }[] {
  const oneMonthReturns = returns?.find(item => item.label === '1 Month Return')
    ?.value
  const threeMonthReturns = returns?.find(
    item => item.label === '3 Month Return',
  )?.value
  const oneYearReturns = returns?.find(item => item.label === '1 Year Return')
    ?.value

  const faqs: { question: string; answer: string }[] = []

  if (name) {
    if (typeof live_price === 'number') {
      faqs.push({
        question: `What is ${name} share price today?`,
        answer: `<p>${name} share price today stands at ₹${live_price}, Open: ₹${day_open}, Previous Close: ₹${prev_close}, High: ₹${day_high}, Low: ₹${day_low}, 52 Week High: ₹${fiftytwo_week_high}, 52 Week Low: ₹${fiftytwo_week_low}.</p>`,
      })
    }

    if (typeof day_high === 'number' && typeof day_low === 'number') {
      faqs.push({
        question: `What are today's High and Low prices of ${name}?`,
        answer: `<ul>
          <li>Today's highest price of ${name} is ₹${day_high}</li>
          <li>Today's lowest price of ${name} is ₹${day_low}</li>
        </ul>`,
      })
    }

    if (
      typeof yearlyHighLow?.max === 'number' &&
      typeof yearlyHighLow.min === 'number'
    ) {
      faqs.push({
        question: `What is the 52 Week High and Low Range of ${name}?`,
        answer: `<div class="text-brand-grey-dark">
          <div class="flex justify-between items-center py-4 border-b border-gray-200 font-semibold">
            <div>${name}</div>
            <div>Price</div>
          </div>
          <div class="flex justify-between items-center py-4 border-b border-gray-200">
            <div>52 Week High</div>
            <div>₹${yearlyHighLow.max}</div>
          </div>
          <div class="flex justify-between items-center pt-4 border-gray-200">
            <div>52 Week Low</div>
            <div>₹${yearlyHighLow.min}</div>
          </div>
        </div>`,
      })
    }

    if (
      typeof day_change === 'number' &&
      typeof oneMonthReturns === 'number' &&
      typeof threeMonthReturns === 'number' &&
      typeof oneYearReturns === 'number'
    ) {
      faqs.push({
        question: `What are the historical returns of ${name}?`,
        answer: `<div class="text-brand-grey-dark">
          <div class="flex justify-between items-center py-4 border-b border-gray-200 font-semibold">
            <div>${name}</div>
            <div>Returns</div>
          </div>
          <div class="flex justify-between items-center py-4 border-b border-gray-200">
            <div>1 Day Returns</div>
            <div>${day_change}%</div>
          </div>
          <div class="flex justify-between items-center py-4 border-b border-gray-200">
            <div>1 Month Returns</div>
            <div>${oneMonthReturns}%</div>
          </div>
          <div class="flex justify-between items-center py-4 border-b border-gray-200">
            <div>3 Month Returns</div>
            <div>${threeMonthReturns}%</div>
          </div>
          <div class="flex justify-between items-center pt-4">
            <div>1 Year Returns</div>
            <div>${oneYearReturns}%</div>
          </div>
        </div>`,
      })
    }

    if (typeof pe === 'number') {
      faqs.push({
        question: `What is the current price to earning (PE) ratio of ${name}?`,
        answer: `<p>${name} price to earning (PE) ratio as on today is ${pe}</p>`,
      })
    }

    faqs.push({
      question: `How can I invest in ${name}?`,
      answer: `<p>You can invest via your demat account in the ${name}. You can invest either through SIP mode or lumpsum mode.</p>`,
    })

    faqs.push({
      question: `Can I invest in ${name} through a Systematic Investment Plan (SIP)?`,
      answer: `<p>Yes. ${name} is available for SIP on INDmoney platform for daily, weekly or monthly SIP.</p>`,
    })

    faqs.push({
      question: `What are the risks associated with investing in ${name}?`,
      answer: `<p>${name}'s drawbacks are not related to specifically ${name}. In Indian context, some of the ETFs have a drawbacks of higher tracking, lower trading volumes, etc.</p>`,
    })

    faqs.push({
      question: `Can I use ${name} as collateral for a loan?`,
      answer: `<p>Yes. ${name} can used as collateral for a loan under Loan Against Securities.</p>`,
    })
  }

  return faqs
}
